import React, { type ChangeEvent, type DragEvent, type FC, useCallback, useMemo, useRef } from "react";
import { cn } from "@heroui/react";

import { FormattedMessage } from "react-intl";

import { Tray } from "@gravity-ui/icons";

type TProps = {
    height?: number;
    onChange: (arg: File[]) => void;
    useSingleDocumentMode: boolean;
};

export const FileUploaderDropzone: FC<TProps> = ({ height, onChange, useSingleDocumentMode }) => {
    const ref = useRef(null);

    const handleChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files);
            onChange(files);
            event.target.value = null;
        },
        [onChange],
    );

    const handleDrag = useCallback(
        async (event: DragEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const files = Array.from(event.dataTransfer.files);
            onChange(files);
        },
        [onChange],
    );

    const handleDragOver = useCallback((event: DragEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleClick = useCallback(() => {
        ref.current.click();
    }, []);

    const containerStyle = useMemo(() => (height ? { height } : undefined), [height]);
    const clickOrDragValues = useMemo(() => ({ isSingular: useSingleDocumentMode }), [useSingleDocumentMode]);

    return (
        <div
            tabIndex={0}
            data-testid="dms-file-uploader-dropzone"
            onClick={handleClick}
            onDrop={handleDrag}
            onDragOver={handleDragOver}
            role="button"
            className={cn(
                "relative flex w-full h-full flex-col items-center justify-center p-4 border border-dashed border-default rounded-lg bg-black/5 cursor-pointer transition-colors duration-300 hover:border-blue-400",
                "h-full",
            )}
            style={containerStyle}
        >
            <Tray className="text-[#1677ff] text-[48px] size-4" />
            <span className="text-[1.1rem] mt-4">
                <FormattedMessage id="app.dms.clickOrDrag" values={clickOrDragValues} />
            </span>
            {!useSingleDocumentMode && (
                <span className="text-muted text-base">
                    <FormattedMessage id="app.dms.supportFor" />
                </span>
            )}

            <input
                ref={ref}
                type="file"
                className="hidden"
                accept="application/pdf, application/xml, text/xml"
                onChange={handleChange}
                multiple={!useSingleDocumentMode}
            />
        </div>
    );
};
